import { CURRENCIES } from '../calc/presets';

const localeFor = (code: string): string =>
  CURRENCIES.find((c) => c.code === code)?.locale ?? 'en-GB';

const cache = new Map<string, Intl.NumberFormat>();

function formatter(key: string, build: () => Intl.NumberFormat): Intl.NumberFormat {
  let f = cache.get(key);
  if (!f) {
    f = build();
    cache.set(key, f);
  }
  return f;
}

/** Whole-unit currency — nobody projecting 37 years cares about the pennies. */
export function formatMoney(value: number, currency: string): string {
  if (!Number.isFinite(value)) return '—';
  const locale = localeFor(currency);
  try {
    return formatter(`m:${locale}:${currency}`, () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }),
    ).format(value);
  } catch {
    return `${currency} ${Math.round(value).toLocaleString(locale)}`;
  }
}

/** Short form for chart axes and tick labels: £842K, £1.2M. */
export function formatMoneyCompact(value: number, currency: string): string {
  if (!Number.isFinite(value)) return '—';
  const locale = localeFor(currency);
  // Sub-million ticks read better whole; millions need a decimal so 1.5M
  // doesn't round into its neighbour. The digit count is part of the cache key.
  const digits = Math.abs(value) < 1_000_000 ? 0 : 1;
  try {
    return formatter(`c:${locale}:${currency}:${digits}`, () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: digits,
      }),
    ).format(value);
  } catch {
    return formatMoney(value, currency);
  }
}

/** 0.055 -> "5.5%" */
export function formatPercent(rate: number, locale = 'en-GB'): string {
  if (!Number.isFinite(rate)) return '—';
  return formatter(`p:${locale}`, () =>
    new Intl.NumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 2,
    }),
  ).format(rate);
}

/**
 * Reads a money field typed by a human, in either separator convention:
 * "50,000", "50.000", "50 000", "50'000", "1,234.56" and "1.234,56" all work.
 *
 * The currency can't tell us which convention is meant (an Irish and a German
 * saver both pick EUR), so the digits decide. A "." or "," followed by groups
 * of exactly three digits is a thousands separator; otherwise it's the decimal
 * point. Nobody enters a pot to three decimal places, so "40.000" is forty
 * thousand, not forty.
 *
 * Returns NaN for anything that isn't a number, so validation can report it.
 */
export function parseMoney(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  // Spaces and apostrophes are only ever thousands separators.
  const cleaned = trimmed.replace(/[\s'\u2019\u00a0\u202f]/g, '');
  const match = MONEY.exec(cleaned);
  if (!match) return Number.NaN;
  const [, sign = '', whole = '', group, point, fraction = ''] = match;
  if (point !== undefined && point === group) return Number.NaN;
  if (whole === '' && fraction === '') return Number.NaN;
  return Number(`${sign}${whole.replace(/[.,]/g, '') || '0'}.${fraction || '0'}`);
}

/**
 * sign, then the whole part (grouped by one consistent "." or ",", or plain
 * digits), then an optional decimal part. Grouping is tried first, so "40.000"
 * is read as grouped; a leading 0 can't start a group, so "0.125" stays a decimal.
 */
const MONEY = /^(-?)([1-9]\d{0,2}(?:([.,])\d{3})(?:\3\d{3})*|\d*)(?:([.,])(\d*))?$/;

export function parseNumber(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return Number.NaN;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : Number.NaN;
}

export interface TextRun {
  text: string;
  emphasis: boolean;
}

/** Splits `*word*` markers out of country copy, so foreign words can be set in italics. */
export function splitEmphasis(text: string): TextRun[] {
  return text
    .split(/\*([^*]+)\*/)
    .map((part, i) => ({ text: part, emphasis: i % 2 === 1 }))
    .filter((run) => run.text !== '');
}
